import {
    ClientId, ISharingInformation, ISharingLinkSettings, Mode,
    ISharingStore
} from '../../interfaces/SharingInterfaces';
import './AtMention.scss';
import { ActivityIndicator } from '../ActivityIndicator/ActivityIndicator';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { Header } from '../Header/Header';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Persona, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { ShareViewState } from '../Share/Share';
import { Spinner, SpinnerType } from 'office-ui-fabric-react/lib/Spinner';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';
import stringsResx from '../Share.resx';
import * as EngagementHelper from '../../utilities/EngagementHelper';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import * as ShareCommunicationManager from '../../utilities/ShareCommunicationManager';

export interface IAtMentionProps {
    clientId: ClientId;
    scenarioId: string;
    currentSettings: ISharingLinkSettings;
    recipients: IPerson[];
    sharingInformation: ISharingInformation;
}

export interface IAtMentionState {
    canShare: boolean;
    isLoading: boolean;
    isSharing: boolean;
}

export class AtMention extends React.Component<IAtMentionProps, IAtMentionState> {
    private _dismiss: () => void;
    private _resize: () => void;
    private _store: ISharingStore;
    private _platformDetection: PlatformDetection;
    private _shareClickTime: number;

    static contextTypes = {
        onDismiss: PropTypes.func.isRequired,
        resize: PropTypes.func.isRequired,
        sharingStore: PropTypes.object.isRequired,
        platformDetection: PropTypes.object.isRequired,
        shareClickTime: PropTypes.number
    };

    constructor(props: IAtMentionProps, context: any) {
        super(props);

        this._dismiss = context.onDismiss;
        this._resize = context.resize;
        this._store = context.sharingStore;
        this._platformDetection = context.platformDetection;
        this._shareClickTime = context.shareClickTime;

        const isLoading = props.recipients.length === 0;
        if (!isLoading) {
            this._notifyReady();
        }

        this.state = {
            canShare: this._canShare(props.recipients),
            isLoading: isLoading,
            isSharing: false
        };
    }

    public componentWillReceiveProps(newProps: IAtMentionProps) {
        // Determine if we're still loading.
        const isStillLoading = newProps.recipients.length === 0;

        // If we were just loading, and now we're not, inform the host.
        if (this.state.isLoading && !isStillLoading) {
            this._notifyReady();
        }

        this.setState({
            canShare: this._canShare(newProps.recipients),
            isLoading: isStillLoading
        });
    }

    public componentDidUpdate() {
        setTimeout(this._resize, 10);
    }

    public componentDidMount() {
        this._notifyReadyForRecipients();

        this._store.addListener(() => {
            const grantAccessResult = this._store.getGrantAccessResult();

            /**
             * If there's no result yet, the result will be null. Once
             * we get a non-null result, inform the host.
             */
            if (grantAccessResult !== null) {
                this._notifyGrantAccessCompleted(grantAccessResult);
                this._dismiss();
            }
        });
    }

    public render(): React.ReactElement<{}> {
        const {
            canShare,
            isLoading,
            isSharing
        } = this.state;

        let visibleContent;

        if (isLoading) {
            visibleContent = this._renderLoadingSpinner();
        } else if (canShare) {
            visibleContent = this._renderHappyPath();
        } else {
            visibleContent = this._renderSadPath();
        }

        return (
            <div className={ `od-AtMention ${isSharing ? 'od-AtMention--isSharing' : ''}` }>
                { !isLoading && (
                    <Header
                        mode={ Mode.atMention }
                        viewState={ ShareViewState.grantPermissions }
                    />
                ) }
                { visibleContent }
                { this._renderSharingActivityIndicator() }
                <span role='alert' className='od-Share-screenReaderOnly'>{ this._getScreenReaderAlertMessage() }</span>
            </div>
        );
    }

    private _renderLoadingSpinner() {
        return (
            <div className='od-AtMention-spinnerHolder'>
                <Spinner
                    className='od-AtMention-spinner'
                    label={ stringsResx.componentLoading }
                    type={ SpinnerType.large }
                />
            </div>
        );
    }

    private _renderSharingActivityIndicator() {
        if (this.state.isSharing) {
            return (
                <ActivityIndicator message={ stringsResx.grantAccessActivityIndicatorString } />
            );
        }
    }

    private _renderHappyPath() {
        const cssClass = this._platformDetection.isIOS ? 'od-AtMention-button-rounded' : 'od-AtMention-button';

        return (
            <div className='od-AtMention-container'>
                { this._renderRecipients(false) }
                <div className='od-AtMention-message'>
                    { stringsResx.atMentionHappyMessage }
                </div>
                <PrimaryButton
                    autoFocus
                    className={ cssClass }
                    onClick={ this._onShareClicked }
                >{ stringsResx.shareAndNotifyButtonLabel }</PrimaryButton>
            </div>
        );
    }

    private _renderSadPath() {
        const cssClass = this._platformDetection.isIOS ? 'od-AtMention-button-rounded' : 'od-AtMention-button';

        return (
            <div className='od-AtMention-container'>
                <MessageBar messageBarType={ MessageBarType.warning }>
                    { this._getSadMessage() }
                </MessageBar>
                { this._renderRecipients(true) }
                <PrimaryButton
                    autoFocus
                    className={ cssClass }
                    onClick={ this._onOkayClicked }
                >{ stringsResx.okayButtonLabel }</PrimaryButton>
            </div>
        );
    }

    private _renderRecipients(isError: boolean): JSX.Element {
        const recipients = this.props.recipients.map((recipient: IPerson) => {
            const isRecipientInvalid = this._isRecipientInvalid(recipient);
            return (
                <li>
                    <Persona
                        className={ `od-AtMention-recipient ${isRecipientInvalid ? 'od-AtMention-recipient--error' : ''}` }
                        imageUrl={ recipient.image }
                        primaryText={ recipient.name }
                        size={ PersonaSize.extraSmall }
                    />
                </li>
            );
        });

        return (
            <ul
                className={ `od-AtMention-recipients ${isError ? 'od-AtMention-recipients--error' : ''}` }
                aria-label={ stringsResx.grantAccessRecipientsListLabel }
            >
                { recipients }
            </ul>
        );
    }

    @autobind
    private _onShareClicked() {
        this.setState({
            isSharing: true
        }, () => {
            EngagementHelper.shareCompleted({
                clientId: this.props.clientId,
                scenarioId: this.props.scenarioId,
                mode: Mode.atMention
            });

            this._store.grantAccess(this.props.recipients);
        });
    }

    @autobind
    private _onOkayClicked() {
        this._notifyGrantAccessCompleted(false);
        this._dismiss();
    }

    private _getScreenReaderAlertMessage(): string {
        if (this.state.isSharing) {
            return stringsResx.grantAccessActivityIndicatorString;
        } else {
            return stringsResx.grantAccessPageLabel;
        }
    }

    private _canShare(recipients: IPerson[]) {
        const {
            sharingInformation
        } = this.props;

        if (!sharingInformation.canManagePermissions) {
            return false;
        }

        if (!sharingInformation.canAddInternalPrincipal) {
            return false;
        }

        return recipients.every((recipient: IPerson) => !this._isRecipientInvalid(recipient));
    }

    private _isRecipientInvalid(recipient: IPerson): boolean {
        return !recipient.isResolved || (recipient.isExternal && !recipient.providerName);
    }

    private _notifyReady() {
        ShareCommunicationManager.pageFinishedLoading(this._shareClickTime, this.props.clientId, Mode.atMention, this.props.scenarioId);
    }

    private _notifyReadyForRecipients() {
        ShareCommunicationManager.readyForRecipients();
    }

    private _notifyGrantAccessCompleted(grantAccessResult: boolean) {
        ShareCommunicationManager.grantAccessCompleted(grantAccessResult);
    }

    private _getSadMessage(): string {
        const {
            sharingInformation
        } = this.props;

        if (sharingInformation.canManagePermissions) {
            if (!sharingInformation.canAddInternalPrincipal) {
                return stringsResx.atMentionSadMessageOwner;
            }
            return stringsResx.atMentionSadMessageRecipient;
        }
        return stringsResx.atMentionSadMessageOwner;
    }
}